import CloseIcon from '@material-ui/icons/Close';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import {
  ERROR_CODE_LABEL,
  EXPORT_FAILURE_LOG_TITLE,
  EXPORT_ID_LABEL,
  EXPORT_TYPE_LABEL,
  LAST_UPDATED_LABEL,
  MESSAGE_LABEL,
  NO_ERROR_DETAILS_AVAILABLE,
  PLATFORM_LABEL,
  VIEW_ERROR_CONTEXT,
} from '../../content';
import { getAudienceAndPlatformTypeStatus } from 'pages/audienceManager/exportHistoryDialog/exportHistoryUtils';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  tooltipContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '650px',
    minHeight: '150px',
    maxHeight: '450px',
    padding: '5px 15px 15px',
    border: '1px solid black',
    backgroundColor: 'white',
    borderRadius: '5px',
    color: 'black',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },
  tooltipContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  errorItemList: {
    marginLeft: '-25px',
    marginTop: '5px',
    marginBottom: '5px',
  },
  tooltipHeader: {
    fontWeight: 'bold',
    fontSize: '16px',
    paddingBottom: '5px',
    lineHeight: '18px',
    fontFamily: 'Calibre-Regular, sans-serif',
  },
  tooltipItemHeader: {
    fontWeight: 'bold',
    fontSize: '14px',
    lineHeight: '16px',
    fontFamily: 'Calibre-Regular, sans-serif',
  },
  tooltipItemText: {
    color: 'gray',
    fontSize: '14px',
    lineHeight: '16px',
    fontFamily: 'Calibre-Regular, sans-serif',
    marginLeft: '5px',
  },
  popoverFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  lastUpdatedText: {
    fontSize: '12px',
    fontStyle: 'italic',
    color: 'gray',
    fontFamily: 'Calibre-Regular, sans-serif',
  },
  errorIcon: {
    color: 'red',
  },
  errorDivider: {
    borderTop: '1px solid #e0e0e0',
    margin: '5px 0',
    width: '100%',
  },
  errorItemContainer: {
    marginBottom: '5px',
  },
  expandedSection: {
    marginTop: '8px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    position: 'relative',
  },
  expandedSectionHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  expandedSectionContent: {
    fontSize: '12px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    userSelect: 'text',
    color: '#333',
    overflowY: 'auto',
    maxHeight: '200px',
  },
  actionIcon: {
    cursor: 'pointer',
    color: '#666',
    fontSize: '18px',
    '&:hover': {
      color: '#000',
    },
  },
  showMoreLinkText: {
    fontWeight: 'bold',
    fontSize: '16px',
    fontFamily: 'Calibre-Regular, sans-serif',
    color: '#5041ab',
    cursor: 'pointer',
    '&:hover': {
      color: '#096dd9',
      textDecoration: 'underline',
    },
  },
}));

interface ExportFailureLogPopupProps {
  exportItem?: {
    audience_id?: string;
    export_id?: string;
    error?: Array<{
      error_message?: string;
      error_type?: string;
      http_status_code?: number;
      last_error_at?: string;
      suggested_action?: string;
      // Allow any additional error fields
      [key: string]: any;
    }>;
    // Allow any additional fields
    [key: string]: any;
  };
}

/**
 * Popup component to display detailed export failure information
 * Shows error details from exportItem - now supports multiple errors in array
 */
const ExportFailureLogPopup: React.FC<ExportFailureLogPopupProps> = ({
  exportItem,
}) => {
  const classes = useStyles();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get error array, default to empty array if not provided
  let errorArray = exportItem?.error || [];

  // Ensure popup stays visible on screen
  useEffect(() => {
    const adjustPosition = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const containerWidth = rect.width;
      const containerHeight = rect.height;

      // Check if popup is cut off
      let left = rect.left;
      let top = rect.top;

      // Adjust horizontal position if cut off on right
      if (left + containerWidth > viewportWidth) {
        left = Math.max(0, viewportWidth - containerWidth - 30);
        container.style.left = `${left}px`;
      }

      // Adjust horizontal position if cut off on left
      if (left < 0) {
        left = 10;
        container.style.left = `${left}px`;
      }

      // Adjust vertical position if cut off at bottom
      if (top + containerHeight > viewportHeight) {
        top = Math.max(0, viewportHeight - containerHeight - 10);
        container.style.top = `${top}px`;
      }

      // Adjust vertical position if cut off at top
      if (top < 0) {
        top = 10;
        container.style.top = `${top}px`;
      }
    };

    // Adjust position after render and on window resize
    adjustPosition();

    return () => {
      window.removeEventListener('resize', adjustPosition);
      window.removeEventListener('scroll', adjustPosition, true);
    };
  }, [expandedIndex]);

  // Handle expand/collapse
  const handleToggleExpand = (index: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  // Handle copy error details
  const handleCopyError = async (errorItem: any, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    const errorJson = JSON.stringify(errorItem, null, 2);
    try {
      await navigator.clipboard.writeText(errorJson);
      // Could add a toast notification here if needed
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  // Render each error item
  const renderErrorItem = (errorItem: any, index: number) => {
    const httpCode = errorItem?.http_status_code || '';
    const errorType = errorItem?.error_type || '';

    const errorDetails = {
      errorCode:
        httpCode && errorType
          ? `${httpCode} ${errorType}`
          : httpCode || errorType
          ? `${httpCode}${errorType}`.trim()
          : 'NA',
      message: errorItem?.error_message || 'NA',
      suggestedAction: errorItem?.suggested_action || 'NA',
      lastUpdated: errorItem?.last_error_at
        ? moment(errorItem.last_error_at).format('MM/DD/YYYY hh:mm:ss')
        : 'NA',
      exportId: exportItem?.exportId || exportItem?.export_id || 'NA',
    };

    const isExpanded = expandedIndex === index;

    return (
      <div
        key={index}
        className={classes.errorItemContainer}
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <ul className={classes.errorItemList}>
          <li>
            <span className={classes.tooltipItemHeader}>
              {ERROR_CODE_LABEL}
            </span>
            <span className={classes.tooltipItemText}>
              - {errorDetails.errorCode}
            </span>
          </li>
          <li>
            <span className={classes.tooltipItemHeader}>{MESSAGE_LABEL}</span>
            <span className={classes.tooltipItemText}>
              - {errorDetails.message}
            </span>
          </li>
          {/* 
            <li>
              <span className={classes.tooltipItemHeader}>
                {SUGGESTED_ACTION_LABEL}
              </span>
              <span className={classes.tooltipItemText}>
                - {errorDetails.suggestedAction}
              </span>
            </li> */}
        </ul>
        <div className={classes.popoverFooter}>
          <div
            className={classes.showMoreLinkText}
            onClick={(e) => handleToggleExpand(index, e)}
          >
            {VIEW_ERROR_CONTEXT}
          </div>
          <span className={classes.lastUpdatedText}>
            {LAST_UPDATED_LABEL}: {errorDetails.lastUpdated}
          </span>
        </div>
        {isExpanded && (
          <div
            className={classes.expandedSection}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <div className={classes.expandedSectionHeader}>
              <FileCopyIcon
                className={classes.actionIcon}
                onClick={(e) => handleCopyError(errorItem, e)}
                titleAccess="Copy error details"
              />
              <CloseIcon
                className={classes.actionIcon}
                onClick={(e) => handleToggleExpand(index, e)}
                titleAccess="Close"
              />
            </div>
            <div className={classes.expandedSectionContent}>
              {JSON.stringify(errorItem, null, 2)}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (typeof errorArray === 'object' && !Array.isArray(errorArray)) {
    errorArray = [errorArray];
  }
  const platformType = _.startCase(
    exportItem?.platform_integrated || exportItem?.platformIntegrated
  )
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const audienceExportType = getAudienceAndPlatformTypeStatus(
    [
      {
        ...exportItem,
        exportType: exportItem?.export_type || exportItem?.exportType,
      },
    ],
    true
  );

  return (
    <div
      ref={containerRef}
      className={classes.tooltipContainer}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="false"
    >
      <div className={classes.tooltipHeader}>{EXPORT_FAILURE_LOG_TITLE}</div>
      <div>
        <span className={classes.tooltipItemHeader}>{EXPORT_ID_LABEL}</span>
        <span className={classes.tooltipItemText}>
          - {exportItem?.export_id || exportItem?.exportId || 'NA'}
        </span>
      </div>
      <div>
        <span className={classes.tooltipItemHeader}>{PLATFORM_LABEL}</span>
        <span className={classes.tooltipItemText}>
          - {platformType || 'NA'}
        </span>
      </div>
      <div>
        <span className={classes.tooltipItemHeader}>{EXPORT_TYPE_LABEL}</span>
        <span className={classes.tooltipItemText}>
          - {audienceExportType?.audience_type || 'NA'}
        </span>
      </div>
      {!errorArray?.length && (
        <div className={classes.errorItemContainer}>
          <span className={classes.tooltipItemText}>
            {NO_ERROR_DETAILS_AVAILABLE}
          </span>
        </div>
      )}
      {errorArray?.map((errorItem, index) => (
        <React.Fragment key={index}>
          <div className={classes.errorDivider} />
          {renderErrorItem(errorItem, index)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ExportFailureLogPopup;
